import { useSelector } from 'react-redux';
import { RootState } from '../../../../state/store';
import { useVisibilityObserver } from '../../../../helpers/hooks/useVisibilityObserver';
import BSWidgetLoader from '../bsWidgetLoader';
import WidgetSkeletonPlaceholder from '../../../../components/widgetSkeletonPlaceholder';

interface LazyBSWidgetLoaderProps {
  widget: {
    url: string;
    type: string;
    [key: string]: any;
  };
}

/**
 * Lazy loading wrapper for BSWidgetLoader.
 *
 * For ClickHouse sessions (enable_clickhouse: true), defers loading of the
 * widget until it scrolls into the viewport. Shows a shimmer skeleton until
 * then. This reduces concurrent ClickHouse queries when a Business Summary
 * dashboard has many widgets.
 *
 * For non-ClickHouse sessions, the widget loads immediately (no change).
 *
 * Also defers re-fetching (e.g. on filter changes) for widgets that are
 * currently off-screen, loading them once they come back into view.
 */
const LazyBSWidgetLoader = ({ widget }: LazyBSWidgetLoaderProps) => {
  const { company_details } = useSelector((state: RootState) => state.company);

  const enableLazyLoad = (company_details as any)?.enable_clickhouse === true;

  const { ref, hasBeenVisible, isCurrentlyVisible } = useVisibilityObserver({
    enabled: enableLazyLoad,
    rootMargin: '200px 0px 200px 0px',
  });

  return (
    <div ref={ref} style={{ width: '100%', height: '100%' }}>
      {hasBeenVisible ? (
        <BSWidgetLoader
          widget={widget}
          isCurrentlyVisible={isCurrentlyVisible}
          lazyLoadEnabled={enableLazyLoad}
        />
      ) : (
        <WidgetSkeletonPlaceholder />
      )}
    </div>
  );
};

export default LazyBSWidgetLoader;
